import imageCompression from 'browser-image-compression';
import type { SelectedFile } from '../domain/types';

/**
 * Client-side image preparation.
 *
 * Photos are the payload here, and up to fifteen 10 MB originals would be a
 * ~150 MB upload that fails on the owner's phone before it ever reaches the
 * server. So each image is downscaled and re-encoded in the browser first, which
 * turns a typical phone photo into a few hundred kilobytes with no visible loss
 * at the size a human reviews it.
 *
 * HEIC is the exception. It is what an iPhone shoots by default, and no browser
 * outside Safari can decode it to a canvas — so it cannot be compressed or
 * previewed on the client. It passes through untouched, and the server converts
 * it (see server/src/media). The thumbnail tile falls back to a placeholder.
 *
 * Compression is best-effort: if it throws for any reason, the original file is
 * used. Losing quality is a nuisance; losing the photo is a lost lead.
 */

let idSeed = 0;
const nextId = () => `img${++idSeed}`;

interface CompressOptions {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  useWebWorker: boolean;
  initialQuality: number;
}

const PHOTO_OPTS: CompressOptions = {
  maxSizeMB: 1.6,
  maxWidthOrHeight: 2200,
  useWebWorker: true,
  initialQuality: 0.82,
};

const PLAN_OPTS: CompressOptions = {
  // A plan carries fine lines and text, so it keeps more resolution and quality.
  maxSizeMB: 4,
  maxWidthOrHeight: 3200,
  useWebWorker: true,
  initialQuality: 0.85,
};

export function isHeicFile(file: File): boolean {
  return /image\/(heic|heif)/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);
}

async function compress(file: File, opts: CompressOptions): Promise<File> {
  if (isHeicFile(file)) return file;
  try {
    const out = await imageCompression(file, opts);
    // Normalise to a real File with the original name — some versions return a
    // bare Blob, which loses the filename the operator sees.
    return new File([out], file.name, { type: out.type || file.type, lastModified: file.lastModified });
  } catch {
    return file;
  }
}

async function toSelected(file: File, opts: CompressOptions): Promise<SelectedFile> {
  const prepared = await compress(file, opts);
  // Object URLs work for any blob; a HEIC one simply fails to decode in the
  // <img>, and the tile's onError swaps in the placeholder.
  return { id: nextId(), file: prepared, previewUrl: URL.createObjectURL(prepared) };
}

export function toSelectedPhoto(file: File): Promise<SelectedFile> {
  return toSelected(file, PHOTO_OPTS);
}

export function toSelectedPlan(file: File): Promise<SelectedFile> {
  return toSelected(file, PLAN_OPTS);
}

/** Release a preview's object URL. Call on removal and on unmount. */
export function revokePreview(entry: SelectedFile): void {
  if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
}
